import type { AccountBalance, AccountStatus, RecoveryCodeStatus, TopupStatus } from './types';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'muted';

export const STATUS_VARIANT: Record<AccountStatus, BadgeVariant> = {
  available: 'success',
  funded: 'secondary',
  // Âmbar = ação pendente (importar), coerente com TOPUP_VARIANT.reserved.
  game_purchased: 'warning',
  in_use: 'default',
  exhausted: 'muted',
  suspended: 'warning',
  banned: 'destructive',
  retired: 'muted',
};

export const STATUS_LABEL: Record<AccountStatus, string> = {
  available: 'Disponível',
  funded: 'Com saldo',
  game_purchased: 'Jogo comprado',
  in_use: 'Importada / Em uso',
  exhausted: 'Esgotada',
  suspended: 'Suspensa',
  banned: 'Banida',
  retired: 'Aposentada',
};

export const RECOVERY_VARIANT: Record<RecoveryCodeStatus, BadgeVariant> = {
  available: 'success',
  assigned: 'default',
  used: 'muted',
  lost: 'destructive',
};

export const RECOVERY_LABEL: Record<RecoveryCodeStatus, string> = {
  available: 'Disponível',
  assigned: 'Atribuído',
  used: 'Usado',
  lost: 'Perdido',
};

export const TOPUP_VARIANT: Record<TopupStatus, BadgeVariant> = {
  available: 'success',
  reserved: 'warning',
  redeemed: 'muted',
  invalid: 'destructive',
  expired: 'destructive',
};

export const TOPUP_LABEL: Record<TopupStatus, string> = {
  available: 'Disponível',
  reserved: 'Reservado',
  redeemed: 'Usado',
  invalid: 'Inválido',
  expired: 'Expirado',
};

/**
 * Formata valor monetário centralizado (Q-246). Aceita string porque
 * numeric(10,2) do Postgres pode chegar serializado como string.
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency: string = 'BRL',
): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(n);
  } catch {
    // Currency code fora da ISO 4217 — degrada sem quebrar a tela.
    return `${currency} ${n.toFixed(2)}`;
  }
}

/** Saldo disponível de uma conta em texto ("US$ 42,10 · R$ 15,00"); "—" se zero. */
export function formatBalances(balance: AccountBalance[] | null | undefined): string {
  const parts = (balance ?? [])
    .filter((b) => Number(b.available) > 0)
    .map((b) => formatMoney(b.available, b.currency));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'agora';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m atrás`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h atrás`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d atrás`;
  return formatDate(iso);
}
